import Link from "next/link";
import { Trans, TFunction, useTranslation } from "next-i18next";
import { Event as HotjarEvent, hotjar } from "@modules/core/hotjar/types";
import { gtmPushData, vehicleInfoEqualityChecker } from "@talladega/helpers";
import { Dispatch, useEffect, useState } from "react";
import { NameId } from "@common/types/NameId";
import { useNumberMask } from "@common/hooks/useMask";
import { toastPop } from "@common/components/Toast";
import { useRouter } from "next/router";
import {
  ConfirmModal,
  LoadingSpinner,
  DisclosureStep,
  DisclosureStepLabel,
  DisclosureStepContent,
  Stepper,
  InfoModal,
  OverlayLoader,
} from "@talladega/components";
import useSessionStorage from "@common/hooks/useSessionStorage";
import { useVinDecodeMutation } from "@modules/vehicle/hooks/useVinDecodeQuery";
import { VinDecodeResponse } from "@modules/vehicle/types/VinDecodeResponse";
import { VehicleBaseInfo, ProvinceEnum } from "@talladega/types";
import {
  TUserType,
  BillOfSaleFormState,
  BillOfSaleStepAction,
  BillOfSaleStepActions,
  BillOfSaleSteps,
  stepsIndex,
  UserType,
} from "../hooks/useBillOfSaleFormStepsReducer";
import BuyerStep from "./BuyerStep";
import SellerStep from "./SellerStep";
import {
  BuyerSellerFieldsInput,
  FindMyVehicleInformationFormInput,
  SourceInformationFieldsInput,
  VehicleInformationFieldsInput,
} from "../types/BillOfSaleFormFields";
import useBillOfSaleDefaultInformation, {
  BillOfSaleDefaultInformation,
} from "../hooks/useBillOfSaleDefaultInformation";
import DownloadBillOfSaleStep from "./DownloadBillOfSaleStep";
import UserInformationStep from "./UserInformationStep";
import {
  SourceInformationRequest,
  VehicleInformationRequest,
} from "../types/CreatePrivateBillOfSaleRequest";
import useCreatePrivateBillOfSaleMutation from "../hooks/useCreatePrivateBillOfSaleMutation";
import VehicleDetailsStep from "./VehicleDetailsStep";

type Props = {
  billOfSaleReducer: [BillOfSaleFormState, Dispatch<BillOfSaleStepAction>];
};

const stepsLength = Object.keys(stepsIndex).length;

const getStepLabel = (step: BillOfSaleSteps, t: TFunction) => {
  return `${t("step_number", { ns: "common", number: stepsIndex[step] })}`;
};

const BillOfSaleForm = ({ billOfSaleReducer }: Props) => {
  const { t } = useTranslation(["billOfSalePage", "common"]);
  const [defaultFormInformation, setDefaultFormInformation] =
    useBillOfSaleDefaultInformation();
  const router = useRouter();
  const locale = router.locale ?? "en";
  const [bosInfoFromStorage, setBosInfoFromStorage] =
    useSessionStorage<BillOfSaleDefaultInformation>("bosInfo");
  const [isTaxTransferFormModalOpen, setIsTaxTransferFormModalOpen] =
    useState<boolean>(false);
  const [isConfirmVinModalOpen, setIsConfirmVinModalOpen] =
    useState<boolean>(false);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const useCreatePrivateBillOfSale = useCreatePrivateBillOfSaleMutation();
  const useVinDecode = useVinDecodeMutation();
  const [formVin, setFormVin] = useState<string>();
  const { unMaskPipe: unMaskNumberPipe } = useNumberMask();

  const [billOfSaleFormState, dispatch] = billOfSaleReducer;
  const {
    activeStep,
    isUserInformationStepComplete,
    isVehicleDetailsStepComplete,
    isBuyerSellerInformationStepComplete,
    formInformation,
    privateBillOfSaleId,
    userType,
    isVinDecodedFromQueryParams,
    vinDecodedInfo,
  } = billOfSaleFormState;

  const isUserSelling = userType === null || userType === UserType.Seller;

  useEffect(() => {
    if (!router.isReady || !defaultFormInformation) return;

    if (Object.keys(router.query).length > 0) {
      setBosInfoFromStorage(defaultFormInformation);
    } else if (
      bosInfoFromStorage &&
      Object.keys(bosInfoFromStorage).length > 0
    ) {
      setDefaultFormInformation(bosInfoFromStorage);
    }
  }, [
    bosInfoFromStorage,
    defaultFormInformation,
    router.isReady,
    router.query,
    setBosInfoFromStorage,
    setDefaultFormInformation,
  ]);

  useEffect(() => {
    if (!defaultFormInformation) return;

    const populateFormWithKijijiQuery = () => {
      dispatch({
        type: BillOfSaleStepActions.SaveFormInformation,
        payload: {
          vehicleDetails: defaultFormInformation.vehicleDetails,
          sellerInformation: defaultFormInformation.sellerInformation,
          buyerInformation: defaultFormInformation.buyerInformation,
          sourceInformation: defaultFormInformation.sourceInformation,
        },
      });
    };

    populateFormWithKijijiQuery();
  }, [defaultFormInformation, dispatch]);

  const evaluateVinDecodeData = (
    vinDecodeData?: VinDecodeResponse
  ): boolean => {
    if (!vinDecodeData || !defaultFormInformation) return false;

    const decodedData: VehicleBaseInfo = {
      year: vinDecodeData.year,
      make: vinDecodeData.make,
      model: vinDecodeData.model,
    };
    const { vehicleDetails } = defaultFormInformation;
    const vehicleDataFromQuery: VehicleBaseInfo = {
      year: vehicleDetails.year ?? 0,
      make: vehicleDetails.make,
      model: vehicleDetails.model,
    };

    return vehicleInfoEqualityChecker(decodedData, vehicleDataFromQuery);
  };

  const isVehicleVinMatch = (vinDecodeData?: VinDecodeResponse): boolean => {
    const oldVin = formInformation?.vehicleDetails.vin;
    const newVin = vinDecodeData?.vin;
    const ymmtFromQueryExist =
      defaultFormInformation?.vehicleDetails.year != null &&
      (defaultFormInformation?.vehicleDetails.make ?? null) != null &&
      (defaultFormInformation?.vehicleDetails.model ?? null) != null;
    const shouldEvaluateVehicleInfo =
      oldVin !== newVin || !isVinDecodedFromQueryParams;

    if (ymmtFromQueryExist && shouldEvaluateVehicleInfo) {
      const isMatch = evaluateVinDecodeData(vinDecodeData);
      return isMatch;
    }

    return true;
  };

  const saveVinDecodedDataToFormInformation = (data?: VinDecodeResponse) => {
    const vinDecodeData = (data ?? vinDecodedInfo) as VinDecodeResponse;
    const isVINChanged =
      vinDecodeData.vin !== formInformation.vehicleDetails.vin;

    if (!vinDecodeData || !defaultFormInformation) return;

    const trimFromQueryParam =
      defaultFormInformation.vehicleDetails.trim ||
      formInformation.vehicleDetails.trim;

    const fuelTypeFromQueryParam =
      defaultFormInformation.vehicleDetails.fuelType ||
      formInformation.vehicleDetails.fuelType;

    let matchedTrim;
    let trimDisplayValue =
      vinDecodeData.trims != null && vinDecodeData.trims?.length === 1
        ? vinDecodeData.trims[0].name
        : "";

    if (trimFromQueryParam && vinDecodeData.trims) {
      // when we have trim from query param, check if it matches decoded trims
      matchedTrim = vinDecodeData.trims.find(
        (trim: NameId) => trimFromQueryParam === trim.name
      );
      trimDisplayValue = matchedTrim?.name ?? trimDisplayValue;
    }

    let matchedFuelType;
    const fuelType =
      vinDecodeData.fuelTypes != null && vinDecodeData.fuelTypes?.length === 1
        ? vinDecodeData.fuelTypes[0].name
        : "";

    if (fuelTypeFromQueryParam && vinDecodeData.fuelTypes) {
      matchedFuelType = vinDecodeData.fuelTypes.find(
        (f) => fuelTypeFromQueryParam === f.name
      );
    }

    dispatch({
      type: BillOfSaleStepActions.SaveFormInformation,
      payload: {
        vehicleDetails: {
          vin: vinDecodeData.vin ?? "",
          year: vinDecodeData.year,
          make: vinDecodeData.make ?? "",
          model: vinDecodeData.model ?? "",
          trim: trimDisplayValue,
          trimNameId: matchedTrim,
          bodyType:
            vinDecodeData.bodyTypes != null &&
            vinDecodeData.bodyTypes?.length === 1
              ? vinDecodeData.bodyTypes[0].name
              : "",
          fuelType,
          fuelTypeNameId: matchedFuelType,
          exteriorColour: isVINChanged
            ? ""
            : formInformation.vehicleDetails.exteriorColour,
          odometer: isVINChanged
            ? null
            : formInformation.vehicleDetails.odometer,
          sellingPrice: isVINChanged
            ? null
            : formInformation.vehicleDetails.sellingPrice,
          vehicleSoldDate: isVINChanged
            ? null
            : formInformation.vehicleDetails.vehicleSoldDate,
          listPrice: formInformation.vehicleDetails.listPrice,
        },
      },
    });
  };

  const handleCreatePrivateBillOfSaleSuccess = (
    formData: FindMyVehicleInformationFormInput & VehicleInformationFieldsInput,
    id: string
  ) => {
    dispatch({
      type: BillOfSaleStepActions.SaveFormInformation,
      payload: {
        vehicleDetails: formData,
      },
    });
    dispatch({
      type: BillOfSaleStepActions.CompleteVehicleDetailsStep,
      payload: true,
    });
    dispatch({
      type: BillOfSaleStepActions.SetActiveStep,
      payload: stepsIndex["buyer-seller-information"],
    });
    dispatch({
      type: BillOfSaleStepActions.SetPrivateBillOfSaleId,
      payload: id,
    });

    gtmPushData({
      event: "BOS-Form-Activity",
      element: "Vehicle Details",
      description: "Vehicle Details Complete",
      bosVin: formData.vin,
      bosId: privateBillOfSaleId || "",
    });

    hotjar.event(HotjarEvent.BillOfSaleVehicleDetails);
  };

  const createPrivateBillOfSale = (formInfo: {
    vehicleInformation: FindMyVehicleInformationFormInput &
      VehicleInformationFieldsInput;
    sourceInformation: SourceInformationFieldsInput;
  }) => {
    const { vehicleInformation, sourceInformation } = formInfo;

    const vehicleInfoRequest: VehicleInformationRequest = {
      ...vehicleInformation,
      vin: vehicleInformation.vin,
      year: vehicleInformation.year as number,
      odometer: unMaskNumberPipe(vehicleInformation.odometer?.toString() ?? ""),
      sellingPrice: Number(
        unMaskNumberPipe(vehicleInformation.sellingPrice?.toString() ?? "")
      ),
      listPrice: Number(
        unMaskNumberPipe(vehicleInformation.listPrice?.toString() ?? "")
      ),
    };

    const sourceInfoRequest: SourceInformationRequest = {
      ...sourceInformation,
    };

    useCreatePrivateBillOfSale.mutate(
      {
        privateBillOfSaleDetails: {
          vehicleInformation: vehicleInfoRequest,
          sourceInformation: sourceInfoRequest,
        },
        locale: router.locale ?? "en",
      },

      {
        onSuccess: (response) => {
          handleCreatePrivateBillOfSaleSuccess(
            vehicleInformation,
            response?.privateBillOfSaleId ?? ""
          );
        },

        onError: () => {
          toastPop(
            "warning",
            t("common:something_went_wrong"),
            t("billOfSalePage:private_bill_of_sale_failed_message")
          );
        },
      }
    );
  };

  const handleUserInformationStepOnNext = (
    formData: BuyerSellerFieldsInput
  ) => {
    const saveFormInformationPayload = isUserSelling
      ? { sellerInformation: formData }
      : { buyerInformation: formData };

    dispatch({
      type: BillOfSaleStepActions.SaveFormInformation,
      payload: saveFormInformationPayload,
    });
    dispatch({
      type: BillOfSaleStepActions.CompleteUserInformationStep,
      payload: true,
    });
    dispatch({
      type: BillOfSaleStepActions.SetActiveStep,
      payload: stepsIndex["vehicle-details"],
    });

    gtmPushData({
      event: "BOS-Form-Activity",
      element: "User Information",
      description: "User Information Complete",
      bosVin: vinDecodedInfo?.vin || "",
      bosId: privateBillOfSaleId || "",
    });

    hotjar.event(HotjarEvent.BillOfSaleBuyerSellerInfo);
  };

  const handleVehicleDetailsStepOnNext = (
    vehicleInformationInput: FindMyVehicleInformationFormInput &
      VehicleInformationFieldsInput
  ) => {
    useVinDecode.mutate(
      { vin: formVin ?? vehicleInformationInput.vin, localeCode: locale },
      {
        onSuccess: (data) => {
          const isMatch = isVehicleVinMatch(data);

          if (!isMatch) {
            setIsConfirmVinModalOpen(true);
            return;
          }

          const formInfo = {
            vehicleInformation: vehicleInformationInput,
            sourceInformation: formInformation.sourceInformation,
          };

          dispatch({
            type: BillOfSaleStepActions.SetVinDecodedInfo,
            payload: data,
          });

          createPrivateBillOfSale(formInfo);
        },
      }
    );
  };

  const handleBuyerSellerInformationStepOnNext = (
    formData: BuyerSellerFieldsInput
  ) => {
    // in this step, if the user is selling, then the form data for this step is the buyer information
    const saveFormInformationPayload = isUserSelling
      ? { buyerInformation: formData }
      : { sellerInformation: formData };

    dispatch({
      type: BillOfSaleStepActions.SaveFormInformation,
      payload: saveFormInformationPayload,
    });
    dispatch({
      type: BillOfSaleStepActions.CompleteBuyerSellerInformationStep,
      payload: true,
    });
    dispatch({
      type: BillOfSaleStepActions.SetActiveStep,
      payload: stepsIndex["download-section"],
    });

    if (
      formInformation.sellerInformation.province?.id ===
      ProvinceEnum.BritishColombia
    ) {
      setIsTaxTransferFormModalOpen(true);
    }

    gtmPushData({
      event: "BOS-Form-Activity",
      element: "Buyer Seller Information",
      description: "Buyer Seller Information Complete",
      bosVin: vinDecodedInfo?.vin || "",
      bosId: privateBillOfSaleId || "",
    });

    hotjar.event(HotjarEvent.BillOfSaleBuyerSellerInfo);
  };

  const handleUserTypeChange = (bosUserType: TUserType) => {
    dispatch({
      type: BillOfSaleStepActions.SetUserType,
      payload: bosUserType,
    });

    dispatch({
      type: BillOfSaleStepActions.CompleteUserInformationStep,
      payload: false,
    });

    dispatch({
      type: BillOfSaleStepActions.CompleteVehicleDetailsStep,
      payload: false,
    });

    dispatch({
      type: BillOfSaleStepActions.CompleteBuyerSellerInformationStep,
      payload: false,
    });
  };

  const handleUserInformationStepLabelClick = () => {
    dispatch({
      type: BillOfSaleStepActions.SetActiveStep,
      payload: stepsIndex["user-information"],
    });
  };

  const handleVehicleDetailsStepLabelClick = () => {
    if (!isUserInformationStepComplete) return;
    dispatch({
      type: BillOfSaleStepActions.SetActiveStep,
      payload: stepsIndex["vehicle-details"],
    });
  };

  const handleBuyerSellerInformationStepLabelClick = () => {
    if (!isVehicleDetailsStepComplete) return;
    dispatch({
      type: BillOfSaleStepActions.SetActiveStep,
      payload: stepsIndex["buyer-seller-information"],
    });
  };

  const handleBackButton = () => {
    dispatch({
      type: BillOfSaleStepActions.BackStep,
    });
  };

  const handleDownloadBillOfSaleStepLabelClick = () => {
    if (!isBuyerSellerInformationStepComplete) return;
    dispatch({
      type: BillOfSaleStepActions.SetActiveStep,
      payload: stepsIndex["download-section"],
    });

    if (
      formInformation.sellerInformation.province?.id ===
      ProvinceEnum.BritishColombia
    ) {
      setIsTaxTransferFormModalOpen(true);
    }
  };

  const handleVinDecode = (vinDecodeData: VinDecodeResponse | undefined) => {
    if (!vinDecodeData) return;

    dispatch({
      type: BillOfSaleStepActions.SetVinDecodeStatus,
      payload: true,
    });

    dispatch({
      type: BillOfSaleStepActions.SetVinDecodedInfo,
      payload: vinDecodeData,
    });

    const isMatch = isVehicleVinMatch(vinDecodeData);

    if (!isMatch) {
      setIsConfirmVinModalOpen(true);
      return;
    }

    saveVinDecodedDataToFormInformation(vinDecodeData);
  };

  const handleVinChange = (vin: string) => {
    setFormVin(vin);
  };

  const handleVinDoesntMatch = () => {
    setIsConfirmVinModalOpen(false);
    gtmPushData({
      event: "BOS-Form-Activity",
      element: "Vehicle Details",
      description: "VIN Doesn't Match Modal",
      bosVin: vinDecodedInfo?.vin || "",
      bosId: privateBillOfSaleId || "",
    });
    hotjar.event(HotjarEvent.BillOfSaleVinDoesntMatch);
  };

  const onBillOfSaleGenerated = () => {
    setIsPageLoading(false);
  };

  return (
    <div>
      <div
        className={`m-4 sm:my-2 sm:mx-14 ${
          activeStep > 1 ? "lg:mt-6" : "lg:mt-12"
        } lg:mx-12 xl:w-2/3`}
      >
        <h1 className="text-primary-deep text-2xl">
          {t("billOfSalePage:page_title")}
        </h1>
        <p className="mt-1 body-3 leading-6 text-text-light-200">
          {t("billOfSalePage:review_provincial_regulations_info")}
        </p>
      </div>
      <Stepper activeStep={activeStep} stepsLength={stepsLength} linear>
        <div className="flex flex-col">
          <DisclosureStep
            index={stepsIndex["user-information"]}
            key="user-information"
            completed={isUserInformationStepComplete}
          >
            <div className="m-4 sm:my-2 sm:mx-14 lg:my-6 lg:mt-12 lg:mx-12">
              <DisclosureStepLabel
                stepLabel={getStepLabel("user-information", t)}
                header={t("billOfSalePage:tell_us_about_yourself")}
                onClick={handleUserInformationStepLabelClick}
              />

              <DisclosureStepContent>
                <UserInformationStep
                  onUserTypeChange={handleUserTypeChange}
                  onNext={handleUserInformationStepOnNext}
                  formInformation={formInformation}
                  userType={userType}
                />
              </DisclosureStepContent>
            </div>
          </DisclosureStep>

          <DisclosureStep
            index={stepsIndex["vehicle-details"]}
            key="find-my-vehicle"
            completed={isVehicleDetailsStepComplete}
          >
            <div className="m-4 sm:my-2 sm:mx-14 lg:my-6 lg:mx-12">
              <DisclosureStepLabel
                stepLabel={getStepLabel("vehicle-details", t)}
                header={t("common:vehicle_details")}
                onClick={handleVehicleDetailsStepLabelClick}
              />

              <DisclosureStepContent>
                <OverlayLoader
                  isLoading={
                    useVinDecode.isPending ||
                    useCreatePrivateBillOfSale.isPending
                  }
                >
                  <VehicleDetailsStep
                    billOfSaleReducer={billOfSaleReducer}
                    onBack={handleBackButton}
                    onNext={handleVehicleDetailsStepOnNext}
                    onVinDecode={handleVinDecode}
                    onVinChange={handleVinChange}
                  />
                </OverlayLoader>
              </DisclosureStepContent>
            </div>
          </DisclosureStep>

          <DisclosureStep
            index={stepsIndex["buyer-seller-information"]}
            key="buyer-seller-information"
            completed={
              !!privateBillOfSaleId && isBuyerSellerInformationStepComplete
            }
          >
            <div className="m-4 sm:my-2 sm:mx-14 lg:my-6 lg:mx-12">
              <DisclosureStepLabel
                stepLabel={getStepLabel("buyer-seller-information", t)}
                header={
                  isUserSelling
                    ? t("billOfSalePage:buyer_info")
                    : t("billOfSalePage:seller_info")
                }
                onClick={handleBuyerSellerInformationStepLabelClick}
              />

              <DisclosureStepContent>
                {isUserSelling ? (
                  <BuyerStep
                    onBack={handleBackButton}
                    onNext={handleBuyerSellerInformationStepOnNext}
                    formInformation={formInformation}
                  />
                ) : (
                  <SellerStep
                    onBack={handleBackButton}
                    onNext={handleBuyerSellerInformationStepOnNext}
                    formInformation={formInformation}
                  />
                )}
              </DisclosureStepContent>
            </div>
          </DisclosureStep>

          <DisclosureStep
            index={stepsIndex["download-section"]}
            key="download-bill-of-sale"
            completed={isBuyerSellerInformationStepComplete}
          >
            <div className="m-4 sm:my-2 sm:mx-14 lg:my-6 lg:mx-12">
              <DisclosureStepLabel
                stepLabel={getStepLabel("download-section", t)}
                header={t("billOfSalePage:download_bos")}
                onClick={handleDownloadBillOfSaleStepLabelClick}
              />

              <DisclosureStepContent>
                <DownloadBillOfSaleStep
                  onBack={handleBackButton}
                  privateBillOfSaleId={privateBillOfSaleId}
                  formInformation={formInformation}
                  userType={userType ?? UserType.Seller}
                  onGenerateBillOfSaleClick={() => setIsPageLoading(true)}
                  onBillOfSaleGenerated={onBillOfSaleGenerated}
                />
              </DisclosureStepContent>
            </div>
          </DisclosureStep>
        </div>
      </Stepper>
      {isTaxTransferFormModalOpen && (
        <InfoModal
          isOpen={isTaxTransferFormModalOpen}
          onClose={() => setIsTaxTransferFormModalOpen(false)}
          title={t("billOfSalePage:additional_forms_required")}
          primaryButtonText={t("billOfSalePage:ok_great_thanks")}
        >
          <div className="lg:w-[27.25rem] mx-auto">
            <h5 className="text-black">{t("billOfSalePage:noted_in_bc")}</h5>
            <p className="text-base mt-4 text-black">
              <Trans
                t={t}
                i18nKey="billOfSalePage:bc_transfer_tax_form_prompt"
                components={{
                  linkText: (
                    <Link
                      href="https://www.icbc.com/vehicle-registration/sell-vehicle/Pages/Transfer-vehicle-ownership.aspx"
                      target="_blank"
                      rel="noreferrer"
                      className="[&>span]:text-primary-bold underline"
                    />
                  ),
                }}
              />
            </p>
          </div>
        </InfoModal>
      )}
      {isConfirmVinModalOpen && (
        <ConfirmModal
          isOpen={isConfirmVinModalOpen}
          onConfirm={() => {
            // Try re-entering VIN
            handleVinDoesntMatch();
          }}
          onCancel={() => {
            // Use New VIN
            saveVinDecodedDataToFormInformation();
            handleVinDoesntMatch();
          }}
          onOutboundClick={() => handleVinDoesntMatch()}
          buttonDisplay="block"
          title={t("billOfSalePage:vehicle_doesnt_match")}
          primaryButtonText={t("billOfSalePage:try_enter_vin_again")}
          secondaryButtonText={t("billOfSalePage:use_vin_and_continue")}
          secondaryButtonFill="link"
        >
          <div className="lg:w-[27.25rem] mx-auto">
            <h5 className="text-black">
              {t("billOfSalePage:vin_dont_match_kijiji")}
            </h5>
            <p className="text-base mt-4 text-black">
              {t("billOfSalePage:try_enter_vin_again_or_continue")}
            </p>
          </div>
        </ConfirmModal>
      )}
      <LoadingSpinner isOverlay isLoading={isPageLoading} />
    </div>
  );
};

export default BillOfSaleForm;
